import { Link } from "react-router-dom";
import { RotateCcw, BookOpen, CheckCircle2, XCircle, Award } from "lucide-react";
import { Button } from "@/components/ui/button";

const LETTERS = ["A", "B", "C", "D"];

const ScoreRing = ({ pct }) => {
  const r = 52, c = 2 * Math.PI * r;
  const color = pct >= 80 ? "var(--chart-top)" : pct >= 60 ? "var(--chart-good)" : "var(--chart-warn)";
  return (
    <div className="relative h-36 w-36">
      <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
        <circle cx="60" cy="60" r={r} stroke="var(--chart-grid)" strokeWidth="10" fill="none" />
        <circle cx="60" cy="60" r={r} stroke={color} strokeWidth="10" fill="none" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (pct / 100) * c} style={{ transition: "stroke-dashoffset 1s ease" }} />
      </svg>
      <span data-testid="result-percentage" className="absolute inset-0 flex items-center justify-center text-3xl font-extrabold text-ink font-display">{Math.round(pct)}%</span>
    </div>
  );
};

const ReviewItem = ({ r, i }) => (
  <div data-testid={`review-item-${i}`} className="rounded-xl border border-line bg-surface p-5">
    <div className="flex items-start gap-3">
      {r.is_correct ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" /> : <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />}
      <div className="flex-1">
        <p className="font-semibold text-ink">{i + 1}. {r.question}</p>
        <p className="mt-2 text-sm text-ink-2">
          Your answer: <span className={r.is_correct ? "text-emerald-700" : "text-red-600"}>{r.selected != null ? `${LETTERS[r.selected]}. ${r.options[r.selected]}` : "Not answered"}</span>
        </p>
        {!r.is_correct && <p className="text-sm text-ink-2">Correct: <span className="text-emerald-700">{LETTERS[r.correct_index]}. {r.options[r.correct_index]}</span></p>}
        <p className="mt-2 rounded-lg bg-canvas-2 p-3 text-sm text-ink-2">{r.explanation}</p>
      </div>
    </div>
  </div>
);

export default function QuizResult({ attempt, onRetake, retakeLabel = "Retake quiz" }) {
  return (
    <div data-testid="quiz-result" className="mx-auto max-w-3xl fade-up">
      <div className="flex flex-col items-start gap-6 rounded-2xl border border-line bg-surface p-6 sm:flex-row sm:items-center sm:p-8">
        <ScoreRing pct={attempt.percentage} />
        <div className="flex-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-aqua">{attempt.subject_name} · {attempt.topic}</p>
          <h2 data-testid="result-score" className="mt-1 text-3xl font-extrabold text-ink">{attempt.score} / {attempt.total} correct</h2>
          <p className="mt-1 text-sm capitalize text-ink-3">{attempt.difficulty} level{attempt.timed && <span data-testid="result-timing" className="normal-case"> · Timed: {Math.floor(attempt.time_taken_sec / 60)}m {attempt.time_taken_sec % 60}s of {attempt.time_limit_sec / 60} min</span>}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {onRetake && <Button data-testid="quiz-retake-btn" onClick={onRetake} className="rounded-full"><RotateCcw className="mr-2 h-4 w-4" />{retakeLabel}</Button>}
            <Button asChild variant="outline" className="rounded-full"><Link data-testid="result-dashboard-link" to="/dashboard">Dashboard</Link></Button>
          </div>
        </div>
      </div>
      {attempt.new_badges?.length > 0 && (
        <div data-testid="new-badges-banner" className="mt-6 flex flex-col gap-3 rounded-2xl bg-band p-6 text-white fade-up">
          <p className="flex items-center gap-2 font-bold"><Award className="h-5 w-5 text-[var(--brand-cyan)]" />New badge{attempt.new_badges.length > 1 ? "s" : ""} unlocked!</p>
          {attempt.new_badges.map((b) => <p key={b.id} className="text-sm text-slate-200"><b>{b.title}</b> — {b.description}</p>)}
          <Link data-testid="new-badges-link" to="/badges" className="self-start rounded-full bg-aqua-solid px-4 py-2 text-sm font-semibold hover:bg-aqua-solid-hover">View & share</Link>
        </div>
      )}
      {attempt.topics_to_revisit?.length > 0 && (
        <div data-testid="topics-to-revisit" className="mt-6 rounded-2xl border border-amber-200 bg-amber-50/60 p-6">
          <h3 className="mb-3 flex items-center gap-2 font-bold text-ink"><BookOpen className="h-4 w-4" />Topics to revisit</h3>
          <div className="flex flex-wrap gap-2">
            {attempt.topics_to_revisit.map((t) => (
              <Link key={t} data-testid={`revisit-topic-${t}`}
                to={attempt.subject_id !== "daily" ? `/learn?subject=${attempt.subject_id}&topic=${encodeURIComponent(t)}` : "/learn"}
                className="rounded-full border border-amber-300 bg-surface px-3 py-1 text-sm text-amber-900 hover:bg-amber-100">{t}</Link>
            ))}
          </div>
        </div>
      )}
      <h3 className="mb-4 mt-10 text-xl font-bold text-ink">Answer review</h3>
      <div className="flex flex-col gap-3">{attempt.results.map((r, i) => <ReviewItem key={i} r={r} i={i} />)}</div>
    </div>
  );
}
